import {
  Component,
  EventEmitter,
  Input, OnDestroy,
  Output,
  ViewChild
} from '@angular/core';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { ActivatedRoute, NavigationEnd, ParamMap, Router, RouterLink } from '@angular/router';
import { IonSkeletonText } from '@ionic/angular/standalone';
import {
  BehaviorSubject,
  EMPTY,
  ReplaySubject, Subject, Subscription,
  combineLatest,
  firstValueFrom,
  interval, merge,
  of
} from 'rxjs';
import {
  delay,
  filter,
  map,
  switchMap,
  takeUntil,
  tap,
  throttleTime
} from 'rxjs/operators';
import { MaterialImportsModule } from 'src/app/material-imports.module';
import { IThingie } from 'src/app/model/thingie/thingie';
import { ICard, StackCardDialogInterface } from 'src/app/model/thingie/thingie-card';
import { IProjectInfo } from 'src/app/pages/dashboard/dashboard.component';
import { AuthService } from 'src/app/services/auth/auth.service';
import { GraphConfigSeriesDataLoaderService } from 'src/app/services/data/series-data-loader/graph-config-series-data-loader.service';
import { DeviceService } from 'src/app/services/device/device.service';
import { LogService } from 'src/app/services/loggers/logger.service';
import { PlotlyGeneratorProviderService } from 'src/app/services/plotly-generator/plotly-generator-provider.service';
import { TabStorageService } from 'src/app/services/tab-storage/tab-storage.service';
import { ThingieService } from 'src/app/services/thingie/thingie-service.service';
import { UserService } from 'src/app/services/user/user.service';
import {
  DASHBOARD_PAGE_CARDS_PROJECT_STORAGE_KEY,
  DASHBOARD_PAGE_CARDS_SORTING_STORAGE_KEY,
  getDashboardSetting,
  updateDashboardSetting
} from 'src/app/utility/dashboard-helper';
import { DefaultUrls } from 'src/assets/default.urls';
import { getThingieGroupId } from 'src/app/utility/thingie-group-helper';
import { ThingieHelper } from 'src/app/utility/thingie-helper';
import { cardsFromThingieGroup, initializeCards } from 'src/app/utility/thingie/thingie-card-generator';
import { isNotUndefined } from 'src/app/utility/types';
import { SubSink } from 'subsink';
import { DialogSizeMode } from '../../dialogs/dialog/dialog-size-mode.enum';
import { DialogService, OpenDialogRef } from '../../dialogs/dialog/dialog.service';
import { FilterByProjectComponent } from '../../filter-by-project/filter-by-project.component';
import { PaginationFilterPageSizeComponent } from '../../pagination/pagination-filter-page-size/pagination-filter-page-size.component';
import { IGroupedThingies } from '../list-thingies.component';
import { StackCardDialogComponent } from '../stack-card-dialog/stack-card-dialog.component';
import { ThingieCardListComponent } from '../thingie-card-list/thingie-card-list.component';
import { ThingieSortComponent } from '../thingie-sort/thingie-sort.component';

@Component({
  selector: 'app-thingie-card-view',
  templateUrl: './thingie-card-view.component.html',
  styleUrls: ['./thingie-card-view.component.scss'],
  imports: [FilterByProjectComponent, ThingieSortComponent, PaginationFilterPageSizeComponent, MaterialImportsModule, IonSkeletonText, ThingieCardListComponent, RouterLink,]
})
export class ThingieCardViewComponent implements OnDestroy {
  protected readonly urls = DefaultUrls;

  @Input()
  public loadingData = true;

  /**
   * @private
   */
  @ViewChild(ThingieCardListComponent)
  private _thingieCardListComponent?: ThingieCardListComponent;

  @Input()
  public pageSize = 8;

  @Input()
  public groupByExperimentChecked = true;

  @Input()
  public set thingieGroups(groups: IGroupedThingies[]) {
    // make sure the cards are reset to load freshly
    this.shownCards = [];

    // add missing thingies to cards
    const newCards: ICard[] = this.createCardsFromThingieGroups(groups);

    this.cards = newCards;
    this.cards$.next(this.cards);

    // trigger reload of cards
    this.groupByExperiment.next(this.groupByExperiment.value);
  }

  private _thingies: IThingie[] = [];
  private _thingieGroups: IGroupedThingies[] = [];
  public get thingies() {
    return this._thingies;
  }
  @Input()
  public projectInfo?: IProjectInfo[];

  @Output() public callbackFunction: EventEmitter<number> = new EventEmitter();
  @Output() public groupByExperimentCallbackFunction: EventEmitter<boolean> = new EventEmitter();
  @Output() public filterCallbackFunction: EventEmitter<string> = new EventEmitter();


  private groupByExperiment = new BehaviorSubject(this.groupByExperimentChecked);
  protected groupByExperimentChange($event: MatSlideToggleChange) {
    // emit to load again
    this.groupByExperimentCallbackFunction.emit($event.checked);
  }

  protected skeletonRange = Array(5);

  protected cards: ICard[] = [];
  protected shownCards: ICard[] = [];
  protected cards$ = new ReplaySubject<ICard[]>(1);

  private dialogRef?: OpenDialogRef<string | undefined>;
  private dialogRef$ = new ReplaySubject<OpenDialogRef<string | undefined> | undefined>(1);

  protected projectFilter =  'all';
  private currentUserID!: string;

  private subscriptions = new SubSink();
  private dialogData?: StackCardDialogInterface;
  private resizeSubscription?: Subscription;

  private paramMapForward$ = new Subject<ParamMap>();

  private stackPopupThingieId$ = new BehaviorSubject<string | null>(null);

  private defaultPopupMode: 'stacked' | 'single' = 'stacked';

  constructor(
    private tabStorage: TabStorageService,
    private generator: PlotlyGeneratorProviderService,
    private dataloader: GraphConfigSeriesDataLoaderService,
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
    private auth: AuthService,
    private dialogService: DialogService,
    private thingieService: ThingieService,
    private deviceService: DeviceService,
    private logService: LogService
  ) {
    void this.auth.getCurrentUserId().then((currentUserID) => {
      this.currentUserID = currentUserID;
      this.projectFilter = getDashboardSetting(currentUserID, DASHBOARD_PAGE_CARDS_PROJECT_STORAGE_KEY) || 'all';
    });

    this.subscriptions.sink = this
      .router
      .events
      .pipe(
        filter(ev => ev instanceof NavigationEnd),
        tap(ev => {
          const url = '/' + this.route.pathFromRoot
            .map(route => route.snapshot.url)
            .flatMap(urlSegments => urlSegments)
            .map((urlSegment) => urlSegment.path)
            .join('/');

          // If the current URL contains the URL of the dashboard, then
          // pass the parameter map to our parameter-Map check logic for
          // opening the dialog - if the card stack is to-be opened.

          const e = ev as NavigationEnd;
          if (e.url.includes(url)) {
            void firstValueFrom(this.route.paramMap)
              .then(paramsMap => {
                this.paramMapForward$.next(paramsMap);
              });
          }
        })
      )
      .subscribe();

    // Check the parameter map for a hint to open the card-stack dialog
    this.subscriptions.sink = merge(route.paramMap, this.paramMapForward$).pipe(
      map(newParamMap => newParamMap.get('stackThingieId')),
      filter(stackThingieId => stackThingieId !== null),
    )
      .subscribe(this.stackPopupThingieId$);

    const newStackThingieId$ = this.stackPopupThingieId$;

    // When the route params stackThingieId changes and is not null, the dialog needs to open.
    // If the dialog is already open, it should close before it opens.
    // When the dialog is closed by the user (clicking the X or outside), we need to navigate to the dashboard (to the correct tab)
    // When the user navigates to a different page, like an object-detail page, the dialog needs to close
    this.subscriptions.sink = newStackThingieId$.pipe(
      switchMap(id => combineLatest([
        of(id),
        (id) ? ThingieHelper.fetchGroupedDisplayThingie(id, this.thingieService).pipe(
          takeUntil(this.dialogRef$.pipe(filter(val => val !== undefined)))
        ) : of([])
      ])),
    ).pipe(
      switchMap(([stackThingieId, fetchedGroupedThingie]) => {
        let loadedCards: ICard[] = [];
        if (fetchedGroupedThingie.length > 0) {
          // add missing thingies to cards
          loadedCards = this.createCardsFromThingieGroups(fetchedGroupedThingie);
        }

        if (this.dialogRef !== undefined) {
          this.dialogRef.forceClose('noNavigation');
        }

        // For the modal, prepare the main card for stacked view...
        const stackedThingiesCard = loadedCards.find(card => card.isStack && stackThingieId !== null && card.thingieIds.includes(stackThingieId));
        if (stackedThingiesCard === undefined) {
          return EMPTY;
        }

        // ... and the individual cards for "individual-cards view"
        const individualThingieCards = loadedCards.filter(
          card => !card.isStack && stackedThingiesCard.thingieIds.includes(card.thingieIds[0])
        );

        this.dialogData = {
          stackedCard: stackedThingiesCard,
          singleCards: individualThingieCards,
          popupMode: this.defaultPopupMode,
          thingies: this.thingies,
        };

        this.dialogRef = this.dialogService.open({
          component: StackCardDialogComponent,
          data: this.dialogData,
          sizeMode: DialogSizeMode.Maximize,
        });

        this.dialogRef$.next(this.dialogRef);
        this.updateCardsGraphConfigurations([stackedThingiesCard]);

        return this.dialogRef
          .afterClosed()
          .then((res) => {
            this.resizeSubscription?.unsubscribe();
            this.dialogData = undefined;
            this.updateCardsGraphConfigurations([stackedThingiesCard]);

            return res;
          });
      }),
      tap((result) => {
        this.dialogRef = undefined;
        this.dialogRef$.next(this.dialogRef);

        if (result !== 'noNavigation') {
          const tab = route.snapshot.queryParams.tab;
          const query = tab ? `?tab=${tab}` : '';

          void router.navigateByUrl(DefaultUrls.route.dashboardCardsWithQuery(query));
        }
      }),
    )
      .subscribe();

    this.setupCardFiltering();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();

    if (this.resizeSubscription !== undefined) {
      this.resizeSubscription.unsubscribe();
    }
  }

  private setupCardFiltering() {
    this.subscriptions.sink = merge(
      // reload cards when switching from stacked to non-stacked view
      this.groupByExperiment.pipe(
        throttleTime(1000, undefined, { leading: true, trailing: true }),
        // avoid NG100 error
        delay(0)
      ),
      // After 20s only the visible cards will be updated
      interval(20000)
    ).pipe(tap(() => {
      // depending on the grouping toggle, either show stacked and single cards,
      // or all non-stacked cards (but only if resorting is found necessary)

      let filteredCards: ICard[];
      if (this.groupByExperimentChecked === true) {
        filteredCards = this.cards.filter(card => card.isStack);
      } else {
        filteredCards = this.cards.filter(card => !card.isStack);
      }

      // fetch pinned cards
      filteredCards.forEach(card => this.checkForPin(card));

      this.shownCards = filteredCards;
    }))
      .subscribe();
  }

  /**
   * Update the isPinned state of a card, based on user pins
   *
   * @param card the card to update
   */
  private checkForPin(card: ICard) {
    const pinTimes = card.thingieIds
      .map(id => this.userService.getPinnedThingies().get(id))
      .filter(isNotUndefined);
    if (pinTimes.length > 0) {
      if (!card.isPinned) {
        card.isPinned = true;
      }
    } else {
      card.isPinned = false;
    }
  }

  protected paginationPageSizeCallbackFunction(selectedPageSize: number) {
    this.callbackFunction.emit(selectedPageSize);
  }

  protected projectChangeCallbackFunction(projectFilter: string) {
    this.projectFilter = projectFilter;

    updateDashboardSetting(this.currentUserID, DASHBOARD_PAGE_CARDS_PROJECT_STORAGE_KEY, projectFilter);

    this.filterCallbackFunction.emit();
  }

  protected sortsChangeCallbackFunction(sort: string) {
    updateDashboardSetting(this.currentUserID, DASHBOARD_PAGE_CARDS_SORTING_STORAGE_KEY, sort);

    this.filterCallbackFunction.emit();
  }

  protected handleCardNameClick(card: ICard) {
    if (card.isStack) {
      this.stackPopupThingieId$.next(card.thingieIds[0]);
    } else {
      this.dialogRef?.forceClose('noNavigation');
    }
  }

  /**
   * to update the graphs and the cards
   *
   */
  private updateCardsGraphConfigurations(_cards: ICard[]) {
    initializeCards(
      _cards,
      this.tabStorage,
      this.dataloader,
      this.generator,
      this.subscriptions
    );
  }


  /**
   * Reset the cards to an empty list
   */
  public resetCards(): void {
    this._thingieCardListComponent?.resetCardsOnNextUpdate();
    this.cards = [];
  }

  private createCardsFromThingieGroups(groups: IGroupedThingies[]): ICard[] {
    this._thingieGroups = groups;
    this._thingies = groups.flatMap(thingieGroup => thingieGroup.thingies.map(displayThingie => displayThingie.thingie));

    // add missing thingies to cards
    const newCards: ICard[] = [];
    const addedGroupIds: string[] = [];
    this._thingieGroups.forEach(group => {
      let existingCards;
      if (this.groupByExperimentChecked) {
        existingCards = this.cards.filter(card => card.thingieGroupId === group.thingies[0].thingieGroupId);
      } else {
        existingCards = this.cards.filter(card => getThingieGroupId(card.thingieIds) === group.thingies[0].thingieId);
      }

      // If we have multiple thingies in a group, we need an extra (stack-)card for representing the group itself
      const expectedCardCount = group.thingies.length <= 1 ? group.thingies.length : group.thingies.length + 1;

      if (existingCards.length === 0 || existingCards.length < expectedCardCount) {
        newCards.push(...cardsFromThingieGroup(group, this.thingieService));
      } else {
        newCards.push(...existingCards);
      }
      addedGroupIds.push(group.thingies[0].thingieGroupId!);
    });

    // keep cards that were shown previously, but now don't belong to a group anymore (for example after starting a group)
    this.cards
      .map((card, idx) => ({ old: !addedGroupIds.includes(card.thingieGroupId), idx, card }))
      .filter(({ old }) => old === true)
      .forEach(({ idx, card }) => {
        const previousCard = this.cards[idx - 1];
        const newIndexOfPreviousCard = newCards.findIndex((card) => card === previousCard);
        // if findIndex returns -1, insert at the beginning, else after the element. This works automatically.
        newCards.splice(newIndexOfPreviousCard + 1, 0, card);
      });

    return newCards;
  }

}
